import type { TrackPoint } from '../types'
import type { RoutePoint } from '@features/adventures/types/workflow.types'

/** Generate a UUID, falling back to a v4-ish string outside secure contexts. */
export function genId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID()
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

/** Great-circle distance between two lat/lng points, in meters. */
export function haversineMeters(
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
): number {
  const R = 6371000
  const lat1 = (aLat * Math.PI) / 180
  const lat2 = (bLat * Math.PI) / 180
  const dLat = ((bLat - aLat) * Math.PI) / 180
  const dLng = ((bLng - aLng) * Math.PI) / 180
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) * Math.sin(dLng / 2)
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h))
}

/** Total length of a sequence of points, in meters. */
export function pathDistanceMeters(points: Array<Pick<TrackPoint, 'lat' | 'lng'>>): number {
  let total = 0
  for (let i = 1; i < points.length; i++) {
    total += haversineMeters(points[i - 1].lat, points[i - 1].lng, points[i].lat, points[i].lng)
  }
  return total
}

/**
 * Min/max elevation difference (meters) across a track, i.e. the height range
 * between its lowest and highest recorded points. Returns null when fewer than
 * two points carry elevation data (GPS elevation is often unavailable).
 */
export function elevationRangeMeters(points: Array<Pick<TrackPoint, 'elevation'>>): number | null {
  const elevations = points
    .map((p) => p.elevation)
    .filter((e): e is number => typeof e === 'number' && Number.isFinite(e))
  if (elevations.length < 2) return null
  return Math.max(...elevations) - Math.min(...elevations)
}

/** Convert track points to the [lat, lng] tuples the route/GPX utilities use. */
export function toLatLngTuples(points: Array<Pick<TrackPoint, 'lat' | 'lng'>>): [number, number][] {
  return points.map((p) => [p.lat, p.lng])
}

/**
 * Convert track points to route points, carrying recorded elevation as the 3rd
 * tuple slot ([lat, lng, ele]) when present so it survives import into a route
 * section. Points without elevation stay 2D.
 */
export function toRoutePoints(points: Array<Pick<TrackPoint, 'lat' | 'lng' | 'elevation'>>): RoutePoint[] {
  return points.map((p) =>
    typeof p.elevation === 'number' && Number.isFinite(p.elevation)
      ? [p.lat, p.lng, p.elevation]
      : [p.lat, p.lng]
  )
}

/** Human-readable elapsed duration, e.g. "5m 12s" or "1h 04m". */
export function formatDuration(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000))
  const hours = Math.floor(totalSeconds / 3600)
  const minutes = Math.floor((totalSeconds % 3600) / 60)
  const seconds = totalSeconds % 60
  if (hours > 0) {
    return `${hours}h ${String(minutes).padStart(2, '0')}m`
  }
  return `${minutes}m ${String(seconds).padStart(2, '0')}s`
}

/**
 * Project a point onto the segment A→B in a local planar (equirectangular)
 * frame centered on A. Returns the perpendicular distance in meters and the
 * clamped fraction t∈[0,1] of the projection along the segment.
 */
function projectToSegment(
  pLat: number,
  pLng: number,
  aLat: number,
  aLng: number,
  bLat: number,
  bLng: number
): { distance: number; t: number } {
  const mPerDegLat = 110540
  const mPerDegLng = 111320 * Math.cos((aLat * Math.PI) / 180)
  const bx = (bLng - aLng) * mPerDegLng
  const by = (bLat - aLat) * mPerDegLat
  const px = (pLng - aLng) * mPerDegLng
  const py = (pLat - aLat) * mPerDegLat
  const len2 = bx * bx + by * by
  let t = len2 === 0 ? 0 : (px * bx + py * by) / len2
  t = Math.max(0, Math.min(1, t))
  const cx = t * bx
  const cy = t * by
  return { distance: Math.hypot(px - cx, py - cy), t }
}

export interface PathPosition {
  /** Perpendicular distance (m) from the point to the nearest route segment. */
  distanceMeters: number
  /** Distance (m) along the route from the start to the projected point. */
  alongMeters: number
  /** Progress along the route, 0–1. */
  fraction: number
}

/**
 * Snap a point to a polyline route: find the nearest segment by perpendicular
 * distance, then measure how far along the route that projection sits. Drives
 * the "on route" check (distanceMeters) and "% complete" (fraction) in walk
 * mode. Returns null for an empty path.
 */
export function nearestOnPath(
  points: Array<Pick<TrackPoint, 'lat' | 'lng'>>,
  lat: number,
  lng: number
): PathPosition | null {
  if (points.length === 0) return null
  if (points.length === 1) {
    return { distanceMeters: haversineMeters(lat, lng, points[0].lat, points[0].lng), alongMeters: 0, fraction: 0 }
  }

  let cumulative = 0
  let best = { distance: Infinity, along: 0 }
  for (let i = 0; i < points.length - 1; i++) {
    const a = points[i]
    const b = points[i + 1]
    const segLen = haversineMeters(a.lat, a.lng, b.lat, b.lng)
    const { distance, t } = projectToSegment(lat, lng, a.lat, a.lng, b.lat, b.lng)
    if (distance < best.distance) {
      best = { distance, along: cumulative + t * segLen }
    }
    cumulative += segLen
  }

  const fraction = cumulative > 0 ? Math.max(0, Math.min(1, best.along / cumulative)) : 0
  return { distanceMeters: best.distance, alongMeters: best.along, fraction }
}

/** Center of a set of points, for initial map framing. Returns null if empty. */
export function centroid(
  points: Array<Pick<TrackPoint, 'lat' | 'lng'>>
): [number, number] | null {
  if (points.length === 0) return null
  const sum = points.reduce(
    (acc, p) => {
      acc.lat += p.lat
      acc.lng += p.lng
      return acc
    },
    { lat: 0, lng: 0 }
  )
  return [sum.lat / points.length, sum.lng / points.length]
}
